import { useEffect, useState } from "react";

import { useDocument, useInspectionAttributes } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { API_ENDPOINTS } from "../../../../constants";
import { useProject as useProjectQuery } from "../../../../hooks/query";

export default function useContractSigning() {
    const { project } = useProject();
    const { useListById } = useProjectQuery();
    const listProjectByIdSvc = useListById();
    const { useList, useUpdateStatus, useTriggerContractSigning, useGetEmbeddedSignLink } = useDocument();
    const { useListByProjectId: useListAttributes } = useInspectionAttributes();
    const listDocListSvc = useList();
    const updateStatusSvc = useUpdateStatus();
    const triggerSigningSvc = useTriggerContractSigning();
    const getEmbeddedSignLinkSvc = useGetEmbeddedSignLink();
    const listAttributesSvc = useListAttributes();

    const [contractDocument, setContractDocument] = useState<any | null>(null);
    const [workOrderDocument, setWorkOrderDocument] = useState<any | null>(null);
    const [purchaseOrderDocument, setPurchaseOrderDocument] = useState<any | null>(null);
    const [hasGutterWork, setHasGutterWork] = useState<boolean>(false);

    const getDocumentStatus = async () => {
        try {
            if (!project?.id) {
                return;
            }
            const response = await listDocListSvc.mutateAsync({ ProjectId: project.id });
            const contractDocument = response.find((document: any) => document.Name === "CONTRACT");
            if (contractDocument) {
                const imageUrl = API_ENDPOINTS.asset.getImageAsSrc(contractDocument.ThumbnailAssetId);
                contractDocument.thumbnailUrl = imageUrl;
            }
            setContractDocument(contractDocument);

            const workOrder = response.find((document: any) => document.Name === "WORK_ORDER");
            if (workOrder) {
                const imageUrl = API_ENDPOINTS.asset.getImageAsSrc(workOrder.ThumbnailAssetId);
                workOrder.thumbnailUrl = imageUrl;
            }
            setWorkOrderDocument(workOrder);

            const purchaseOrder = response.find((document: any) => document.Name === "SUPPLY_ORDER");
            if (purchaseOrder) {
                const imageUrl = API_ENDPOINTS.asset.getImageAsSrc(purchaseOrder.ThumbnailAssetId);
                purchaseOrder.thumbnailUrl = imageUrl;
            }
            setPurchaseOrderDocument(purchaseOrder);

            const attributesRaw = await listAttributesSvc.mutateAsync({
                ProjectId: project.id,
            });
            const attributes = Array.isArray(attributesRaw)
                ? attributesRaw
                : Array.isArray((attributesRaw as any)?.Data)
                  ? (attributesRaw as any).Data
                  : [];

            // InspectionV2 names (+ legacy fallbacks). Show when gutter work is indicated.
            const getVal = (name: string) => {
                const attr = attributes.find(
                    (a: any) => a?.AttributeName === name,
                );
                return String(attr?.AttributeValue ?? "").trim();
            };
            const numericVal = (...names: string[]) => {
                for (const name of names) {
                    const n = parseFloat(getVal(name));
                    if (!Number.isNaN(n) && n > 0) return n;
                }
                return 0;
            };
            const hasText = (...names: string[]) =>
                names.some((name) => {
                    const val = getVal(name);
                    return val !== "" && val !== "0";
                });

            const gutterAction = getVal("GutterLengthAction").toLowerCase();
            const installAction =
                gutterAction === "replace" ||
                gutterAction === "install" ||
                gutterAction === "detach & reset" ||
                getVal("Install New Gutters").toLowerCase() === "true";

            const hasGutter =
                installAction ||
                numericVal("GutterLengthQty", "Gutter Length") > 0 ||
                hasText("GutterSize", "Gutter Size") ||
                numericVal("DownspoutLengthQty", "Downspout Length") > 0 ||
                hasText("DownspoutSize", "Downspout Size");

            setHasGutterWork(hasGutter);

        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        getDocumentStatus().then();
    }, [project?.id]);

    const markAsSigned = async () => {
        if (!contractDocument?.Id) return;
        await updateStatusSvc.mutateAsync({ DocumentId: contractDocument.Id, Status: "SIGNED" });
        await getDocumentStatus();
    };

    const triggerSigning = async () => {
        try {
            if (!project?.id) return;

            // Get project details to get customer email
            const projectDetails = await listProjectByIdSvc.mutateAsync({ Id: project.id });
            const customerEmail = projectDetails?.CustomerEmail || "";

            if (!customerEmail) {
                throw new Error("Customer email not found");
            }

            await triggerSigningSvc.mutateAsync({
                ProjectId: project.id,
                ToAddress: customerEmail,
            });

            // Refresh document status after sending email
            await getDocumentStatus();
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    const triggerEmbeddedSigning = async (redirectUrl: string = window.location.href) => {
        try {
            if (!project?.id) return null;

            const projectDetails = await listProjectByIdSvc.mutateAsync({ Id: project.id });
            const customerEmail = projectDetails?.CustomerEmail || "";

            if (!customerEmail) {
                throw new Error("Customer email not found");
            }

            await triggerSigningSvc.mutateAsync({
                ProjectId: project.id,
                ToAddress: customerEmail,
                IsEmbeddedSigning: true,
            });

            await getDocumentStatus();
            
            if (!contractDocument?.Id) {
                throw new Error("Contract document not found after generation");
            }

            const response = await getEmbeddedSignLinkSvc.mutateAsync({
                DocumentId: contractDocument.Id,
                SignerEmail: customerEmail,
            });

            return response.signLink;
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    return {
        contractDocument,
        workOrderDocument,
        purchaseOrderDocument,
        hasGutterWork,
        markAsSigned,
        triggerSigning,
        triggerEmbeddedSigning,
        isUpdatingStatus: updateStatusSvc.isPending,
        isTriggeringSigning: triggerSigningSvc.isPending,
        isGettingEmbeddedSignLink: getEmbeddedSignLinkSvc.isPending,
        refreshContract: getDocumentStatus,
    };
}
